import { useRef, useState, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { toast } from 'sonner'
import api from '@/services/api'
import { cnpjMask } from '@/components/Deals/DealDetailPanel/utils'
import { getFriendlyError } from '@/utils/getFriendlyError'
import type { CustomerData } from '@/utils/propostaPublicaUtils'

type Props = {
  customer: CustomerData
  onChange: (patch: Partial<CustomerData>) => void
  cadastroSave?: 'idle' | 'saving' | 'saved' | 'error'
  onSolicitarRevisao?: () => void
}

const inputClass =
  'w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] text-sm rounded-md px-3 py-2.5 outline-none focus:border-[var(--brand-primary)] transition-colors'

const labelClass = 'block text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1'

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-[var(--border)] bg-[var(--bg-dark)]/40 p-3 space-y-3">
      <h3 className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">{title}</h3>
      {children}
    </section>
  )
}

export function PropostaPublicaStep2({ customer, onChange, cadastroSave = 'idle', onSolicitarRevisao }: Props) {
  const [searching, setSearching] = useState(false)
  const lookedUp = useRef('')

  const lookupCnpj = async (digits: string) => {
    if (digits.length !== 14 || digits === lookedUp.current) return
    lookedUp.current = digits
    setSearching(true)
    try {
      const { data } = await api.get(`/propostas/public/cnpj/${digits}`)
      const nome = String(data?.nomeFantasia || data?.razaoSocial || '').trim()
      if (lookedUp.current !== digits) return
      onChange({
        cnpj: cnpjMask(String(data?.cnpj || digits)),
        ...(nome ? { empresa: nome } : {}),
      })
    } catch (err: unknown) {
      lookedUp.current = ''
      toast.error(getFriendlyError(err, 'Não foi possível buscar o CNPJ'))
    } finally {
      setSearching(false)
    }
  }

  return (
    <motion.div
      key="step2"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4 max-w-xl mx-auto"
    >
      <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 sm:p-6 space-y-4 shadow-sm">
        <div>
          <h2 className="text-base font-bold text-[var(--text-primary)]">Revise seu cadastro</h2>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Confira os dados e ajuste o que precisar. O que você preencher é salvo na empresa, sem precisar aceitar a proposta.
          </p>
          {cadastroSave === 'saving' && (
            <p className="text-[11px] text-[var(--text-muted)]">Salvando cadastro...</p>
          )}
          {cadastroSave === 'saved' && (
            <p className="text-[11px] text-[var(--brand-primary)]">Cadastro salvo</p>
          )}
          {cadastroSave === 'error' && (
            <p className="text-[11px] text-red-400">Não foi possível salvar agora. Continue preenchendo.</p>
          )}
        </div>

        <Section title="Assinatura do contrato">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Nome completo <span className="text-red-400">*</span></label>
              <input
                value={customer.nome}
                onChange={(e) => onChange({ nome: e.target.value })}
                placeholder="Quem assina"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>E-mail <span className="text-red-400">*</span></label>
              <input
                type="email"
                value={customer.email}
                onChange={(e) => onChange({ email: e.target.value })}
                placeholder="assinante@empresa.com"
                className={inputClass}
              />
            </div>
          </div>
        </Section>

        <Section title="Empresa">
          <div>
            <label className={labelClass}>Nome</label>
            <input
              value={customer.empresa}
              onChange={(e) => onChange({ empresa: e.target.value })}
              placeholder="Nome da empresa"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>CNPJ <span className="text-red-400">*</span></label>
            <input
              value={customer.cnpj}
              onChange={(e) => {
                const masked = cnpjMask(e.target.value)
                onChange({ cnpj: masked })
                const digits = masked.replace(/\D/g, '')
                if (digits.length === 14) void lookupCnpj(digits)
                else lookedUp.current = ''
              }}
              placeholder="00.000.000/0000-00"
              className={`${inputClass} font-mono tracking-wide`}
            />
            {searching && (
              <p className="mt-1 text-[11px] text-[var(--text-muted)]">Consultando CNPJ...</p>
            )}
          </div>
        </Section>

        <Section title="Contato financeiro">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Nome <span className="text-red-400">*</span></label>
              <input
                value={customer.responsavelFinanceiroNome}
                onChange={(e) => onChange({ responsavelFinanceiroNome: e.target.value })}
                placeholder="Responsável financeiro"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>E-mail <span className="text-red-400">*</span></label>
              <input
                type="email"
                value={customer.emailFinanceiro}
                onChange={(e) => onChange({ emailFinanceiro: e.target.value })}
                placeholder="financeiro@empresa.com"
                className={inputClass}
              />
            </div>
          </div>
        </Section>
        {onSolicitarRevisao && (
          <button
            type="button"
            onClick={onSolicitarRevisao}
            className="text-xs font-medium text-[var(--text-muted)] underline-offset-2 hover:text-[var(--text-primary)] hover:underline"
          >
            Solicitar revisão
          </button>
        )}
      </div>
    </motion.div>
  )
}
